// 剧本生成任务列表页
// 展示所有剧本生成任务，支持筛选和搜索

import React, { useEffect, useState, useCallback } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Progress,
  Input,
  Select,
  message,
  Popconfirm,
  Tooltip,
  Dropdown,
} from 'antd'
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import type { ColumnsType } from 'antd/es/table'
import type { ScriptGenerationTask, ScriptGenerationTaskStatus } from '@/types/scriptGeneration'
import {
  getScriptGenerationTasks,
  deleteScriptGenerationTask,
  cancelScriptGenerationTask,
  retryScriptGenerationTask,
} from '@/services/scriptGeneration'
import {
  STATUS_COLOR,
  STATUS_LABEL,
  INPUT_TYPE_LABEL,
  isRetryableStatus,
  isDeletableStatus,
  isCancellableStatus,
} from '@/constants/scriptGeneration'
import { handleApiError } from '@/utils/errorHandler'
import './TaskList.css'

/**
 * 剧本生成任务列表页
 */
const ScriptGenerationTaskList: React.FC = () => {
  const navigate = useNavigate()
  const [messageApi, contextHolder] = message.useMessage()

  // 状态管理
  const [tasks, setTasks] = useState<ScriptGenerationTask[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // 筛选条件
  const [statusFilter, setStatusFilter] = useState<ScriptGenerationTaskStatus | undefined>()
  const [inputTypeFilter, setInputTypeFilter] = useState<'text' | 'file' | undefined>()
  const [versionFilter, setVersionFilter] = useState<'4.0' | '5.0' | undefined>()
  const [keyword, setKeyword] = useState('')

  /**
   * 加载任务列表
   */
  const loadTasks = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getScriptGenerationTasks({
        page,
        page_size: pageSize,
        status: statusFilter,
        search: keyword || undefined,
        version: versionFilter,
      })
      setTasks(response.tasks)
      setTotal(response.total)
    } catch (error) {
      handleApiError(error, '加载任务列表失败', messageApi)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, statusFilter, inputTypeFilter, versionFilter, keyword, messageApi])

  /**
   * 初始化加载数据
   */
  useEffect(() => {
    loadTasks()
  }, [loadTasks])

  /**
   * 搜索处理
   */
  const handleSearch = useCallback(() => {
    setPage(1)
  }, [])

  /**
   * 重置筛选
   */
  const handleReset = useCallback(() => {
    setStatusFilter(undefined)
    setInputTypeFilter(undefined)
    setVersionFilter(undefined)
    setKeyword('')
    setPage(1)
  }, [])

  /**
   * 查看任务详情
   */
  const handleViewDetail = useCallback((taskId: string) => {
    navigate(`/script-generation/${taskId}`)
  }, [navigate])

  /**
   * 创建任务菜单项
   */
  const createTaskMenuItems = [
    {
      key: 'v4',
      label: 'v4.0 - 文本驱动（传统）',
      onClick: () => navigate('/script-generation/create'),
    },
    {
      key: 'v5',
      label: 'v5.0 - 配置驱动（推荐）',
      onClick: () => navigate('/script-generation/create-v5'),
    },
  ]

  /**
   * 删除任务
   */
  const handleDelete = useCallback(async (taskId: string) => {
    try {
      await deleteScriptGenerationTask(taskId)
      messageApi.success('删除成功')
      loadTasks()
    } catch (error) {
      handleApiError(error, '删除失败', messageApi)
    }
  }, [loadTasks, messageApi])

  /**
   * 取消任务
   */
  const handleCancel = useCallback(async (taskId: string) => {
    try {
      await cancelScriptGenerationTask(taskId)
      messageApi.success('取消成功')
      loadTasks()
    } catch (error) {
      handleApiError(error, '取消失败', messageApi)
    }
  }, [loadTasks, messageApi])

  /**
   * 重试任务
   */
  const handleRetry = useCallback(async (taskId: string) => {
    try {
      await retryScriptGenerationTask(taskId)
      messageApi.success('重试成功')
      loadTasks()
    } catch (error) {
      handleApiError(error, '重试失败', messageApi)
    }
  }, [loadTasks, messageApi])

  /**
   * 表格列定义
   */
  const columns: ColumnsType<ScriptGenerationTask> = [
    {
      title: 'ID',
      dataIndex: 'task_id',
      key: 'task_id',
      width: 80,
      ellipsis: true,
      render: (taskId: number) => (
        <Tooltip title={String(taskId)}>
          <span>#{String(taskId).slice(-6)}</span>
        </Tooltip>
      ),
    },
    {
      title: '任务名称',
      dataIndex: 'task_name',
      key: 'task_name',
      width: 200,
      ellipsis: true,
      render: (text: string, record: ScriptGenerationTask) => (
        <a onClick={() => handleViewDetail(String(record.task_id))}>{text}</a>
      ),
    },
    {
      title: '描述',
      dataIndex: 'task_description',
      key: 'task_description',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
      width: 80,
      render: (version: string) => (
        <Tag color={version === '5.0' ? 'blue' : 'default'}>
          v{version || '4.0'}
        </Tag>
      ),
    },
    {
      title: '输入类型',
      dataIndex: 'input_type',
      key: 'input_type',
      width: 100,
      render: (type: string) => INPUT_TYPE_LABEL[type as keyof typeof INPUT_TYPE_LABEL] || type,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: ScriptGenerationTaskStatus) => (
        <Tag color={STATUS_COLOR[status]}>{STATUS_LABEL[status]}</Tag>
      ),
    },
    {
      title: '当前阶段',
      dataIndex: 'phase_name',
      key: 'phase_name',
      width: 120,
      ellipsis: true,
      render: (phase: string) => phase || '-',
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 120,
      render: (progress: number, record: ScriptGenerationTask) => {
        const isCompleted = record.status === 'completed'
        const isFailed = record.status === 'failed'
        return (
          <Tooltip title={`${progress}%`}>
            <Progress
              percent={progress}
              size="small"
              status={isCompleted ? 'success' : isFailed ? 'exception' : 'active'}
            />
          </Tooltip>
        )
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_: unknown, record: ScriptGenerationTask) => {
        return (
          <Space size="small">
            <Tooltip title="查看详情">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewDetail(String(record.task_id))}
              />
            </Tooltip>

            {isCancellableStatus(record.status) && (
              <Popconfirm
                title="确认取消任务？"
                onConfirm={() => handleCancel(String(record.task_id))}
              >
                <Button type="link" size="small" danger>
                  取消
                </Button>
              </Popconfirm>
            )}

            {isRetryableStatus(record.status) && (
              <Tooltip title="重新生成">
                <Button
                  type="link"
                  size="small"
                  icon={<SyncOutlined />}
                  onClick={() => handleRetry(String(record.task_id))}
                >
                  重试
                </Button>
              </Tooltip>
            )}

            {isDeletableStatus(record.status) && (
              <Popconfirm
                title="确认删除任务？"
                onConfirm={() => handleDelete(String(record.task_id))}
              >
                <Tooltip title="删除">
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <div className="p-6">
      <div className="script-generation-task-list">
        {contextHolder}

        <Card
          title={
            <Space>
              <span>剧本生成任务</span>
              <Tag color="blue">{total} 个任务</Tag>
            </Space>
          }
          extra={
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadTasks} loading={loading}>
                刷新
              </Button>
              <Dropdown menu={{ items: createTaskMenuItems }} placement="bottomRight">
                <Button type="primary" icon={<PlusOutlined />}>
                  创建任务
                </Button>
              </Dropdown>
            </Space>
          }
        >
          {/* 筛选条件 */}
          <Space style={{ marginBottom: 16 }} wrap>
            <Input.Search
              placeholder="搜索任务名称或描述"
              allowClear
              style={{ width: 240 }}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={handleSearch}
            />
            <Select
              placeholder="选择状态"
              allowClear
              style={{ width: 140 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { label: '待处理', value: 'pending' },
                { label: '进行中', value: 'preprocessing' },
                { label: '已完成', value: 'completed' },
                { label: '失败', value: 'failed' },
                { label: '已取消', value: 'cancelled' },
              ]}
            />
            <Select
              placeholder="输入类型"
              allowClear
              style={{ width: 120 }}
              value={inputTypeFilter}
              onChange={setInputTypeFilter}
              options={[
                { label: '文本输入', value: 'text' },
                { label: '文档上传', value: 'file' },
              ]}
            />
            <Select
              placeholder="版本"
              allowClear
              style={{ width: 100 }}
              value={versionFilter}
              onChange={setVersionFilter}
              options={[
                { label: 'v4.0', value: '4.0' },
                { label: 'v5.0', value: '5.0' },
              ]}
            />
            <Button onClick={handleReset}>重置</Button>
          </Space>

          {/* 任务表格 */}
          <Table
            columns={columns}
            dataSource={tasks}
            rowKey="taskId"
            loading={loading}
            scroll={{ x: 1200 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条`,
              onChange: (newPage, newPageSize) => {
                setPage(newPage)
                setPageSize(newPageSize || 10)
              },
            }}
          />
        </Card>
      </div>
    </div>
  )
}

export default ScriptGenerationTaskList
